import { useContext, useEffect } from 'react';

import { ALGOLIA_EVENT_NAME } from '@/integrations/algolia/const';
import { getIndexName } from '@/integrations/algolia/helpers';
import { forgetCachedQueryID } from '@/integrations/algolia/queryCache';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { skuToSlug } from '@/lib/product';
import { LocaleContext } from '@/providers/LocaleProvider/LocaleContext';
import { useAlgoliaInsightsEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';

import { useSendAlgoliaEvent } from './useSendAlgoliaEvent';

const sentLandingSkus = new Set<string>();

/**
 * True when the product page of `sku` is the first page of a fresh page load
 * (not a reload or back/forward) and the visitor did not come from this site.
 * Client-side navigations keep the document's navigation entry, whose URL is
 * the page the visitor landed on.
 */
const isExternalLandingPage = (sku: string) => {
  const [navigation] = (performance.getEntriesByType?.('navigation') ??
    []) as PerformanceNavigationTiming[];
  if (navigation?.type !== 'navigate') return false;

  try {
    const landingPath = decodeURIComponent(new URL(navigation.name).pathname);
    const isProductLanding = skuToSlug(landingPath)
      .toUpperCase()
      .includes(skuToSlug(sku).toUpperCase());
    if (!isProductLanding) return false;
  } catch {
    return false;
  }

  try {
    return new URL(document.referrer).host !== window.location.host;
  } catch {
    return true;
  }
};

/**
 * Sends a product click when a product page is entered from outside the site
 * (e.g. Google Shopping), so the visit counts for Recommend and Personalization,
 * and stops a later conversion being attributed to an older on-site search.
 */
export const useSendLandingAlgoliaEvent = (sku: string | null | undefined) => {
  const isEnabled = useAlgoliaInsightsEnabled();
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  useEffect(() => {
    if (!isEnabled || !sku || sentLandingSkus.has(sku)) return;
    if (!isExternalLandingPage(sku)) return;

    // This effect runs before _app's, which initialises search-insights;
    // sending in the same tick throws and drops the event.
    const timeoutId = setTimeout(() => {
      const indexName = getIndexName('PRODUCT', locale);
      sentLandingSkus.add(sku);
      forgetCachedQueryID(indexName, sku);
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.clickedObjectIDs,
        eventName: ALGOLIA_EVENT_NAME.productDetailViewed,
        indexName,
        objectIDs: [sku],
      });
    });

    return () => clearTimeout(timeoutId);
  }, [sku, isEnabled]);
};
